import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getImageUrl } from '../../utils/imageUtils';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';

// No mock data - using database data only

const InvoiceDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [invoice, setInvoice] = useState<any | null>(null);
  const [companyInfo, setCompanyInfo] = useState<any | null>(null);
  const [invoiceSettings, setInvoiceSettings] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [pdfPreviewContent, setPdfPreviewContent] = useState('');
  const invoiceRef = useRef<HTMLDivElement>(null);
  const pdfPreviewRef = useRef<HTMLDivElement>(null);

  // Reusable calculation functions for consistent calculations across screen and PDF
  const calculateItemSubtotal = (item: any) => {
    const unitPrice = Number(item.unit_price || item.unitPrice) || 0;
    const qtyInBox = Number(item.qty_per_box || item.qtyPerBox) || 1;
    const fulfilledQty = Number(item.fulfilled_quantity || item.fulfilledQuantity || 0);
    const discount = Number(item.discount || 0);
    const orderType = item.order_type || 'box'; // Default to box for backward compatibility
    
    let subtotal;
    
    // Calculate subtotal based on order type
    switch (orderType) {
      case 'single':
        subtotal = (unitPrice * fulfilledQty) - discount;
        break;
      case 'box':
        subtotal = (unitPrice * qtyInBox * fulfilledQty) - discount;
        break;
      case 'both':
        const fulfilledBoxQty = Number(item.fulfilled_box_quantity || 0);
        const fulfilledSingleQty = Number(item.fulfilled_single_quantity || 0);
        const boxSubtotal = fulfilledBoxQty * (unitPrice * qtyInBox);
        const singleSubtotal = fulfilledSingleQty * unitPrice;
        subtotal = boxSubtotal + singleSubtotal - discount;
        break;
      default:
        // Default to single pricing if order type is not properly set
        subtotal = (unitPrice * fulfilledQty) - discount;
    }
    
    return subtotal;
  };

  const calculateItemTotal = (item: any) => {
    const subtotal = calculateItemSubtotal(item);
    const taxRate = Number(item.tax_rate || item.tax || 0);
    const tax = subtotal * (taxRate / 100);
    return subtotal + tax;
  };

  const calculateInvoiceSubtotal = () => {
    let subtotal = 0;
    if (invoice?.items) {
      invoice.items.forEach((item: any) => {
        subtotal += calculateItemSubtotal(item);
      });
    }
    return subtotal;
  };

  const calculateInvoiceTax = () => {
    let taxAmount = 0;
    if (invoice?.items) {
      invoice.items.forEach((item: any) => {
        const itemSubtotal = calculateItemSubtotal(item);
        const taxRate = Number(item.tax_rate || item.tax || 0);
        taxAmount += itemSubtotal * (taxRate / 100);
      });
    }
    return taxAmount;
  };

  const calculateInvoiceRefunds = () => {
    if (!invoice?.refunds) return 0;
    return invoice.refunds.reduce((total: number, refund: any) => {
      return total + parseFloat(refund.refund_amount || 0);
    }, 0);
  };

  const calculateInvoiceTotal = () => {
    return calculateInvoiceSubtotal() + calculateInvoiceTax() - calculateInvoiceRefunds();
  };

  const calculateTotalQuantities = () => {
    let totalItems = 0;
    let totalBoxQty = 0;
    let totalSingleQty = 0;

    if (invoice?.items) {
      invoice.items.forEach((item: any) => {
        totalItems++; // Count each item line
        
        const orderType = item.order_type || 'box';
        const fulfilledQty = Number(item.fulfilled_quantity || item.fulfilledQuantity || 0);
        
        switch (orderType.toLowerCase()) {
          case 'single':
            totalSingleQty += fulfilledQty;
            break;
          case 'box':
            totalBoxQty += fulfilledQty;
            break;
          case 'both':
            const fulfilledBoxQty = Number(item.fulfilled_box_quantity || 0);
            const fulfilledSingleQty = Number(item.fulfilled_single_quantity || 0);
            totalBoxQty += fulfilledBoxQty;
            totalSingleQty += fulfilledSingleQty;
            break;
          default:
            totalBoxQty += fulfilledQty; // Default to box
        }
      });
    }

    return { totalItems, totalBoxQty, totalSingleQty };
  };

  // Helper function to format quantity display for both ordered and fulfilled quantities
  const formatQuantityDisplay = (item: any, isOrdered: boolean = false) => {
    const orderType = item.order_type || 'box';
    
    if (orderType === 'both') {
      if (isOrdered) {
        const orderedBoxQty = Number(item.ordered_box_quantity || item.box_quantity || 0);
        const orderedSingleQty = Number(item.ordered_single_quantity || item.single_quantity || 0);
        return (
          <Box sx={{ textAlign: 'right' }}>
            <div>{orderedBoxQty} (B)</div>
            <div>{orderedSingleQty} (S)</div>
          </Box>
        );
      } else {
        const fulfilledBoxQty = Number(item.fulfilled_box_quantity || 0);
        const fulfilledSingleQty = Number(item.fulfilled_single_quantity || 0);
        return (
          <Box sx={{ textAlign: 'right' }}>
            <div>{fulfilledBoxQty} (B)</div>
            <div>{fulfilledSingleQty} (S)</div>
          </Box>
        );
      }
    } else {
      // For single or box orders, show the regular quantity
      const qty = isOrdered ? 
        (Number(item.quantity || 0)) : 
        (Number(item.fulfilled_quantity || item.fulfilledQuantity || 0));
      return qty;
    }
  };

  // Helper function to format quantity display for PDF (returns string)
  const formatQuantityDisplayPdf = (item: any, isOrdered: boolean = false) => {
    const orderType = item.order_type || 'box';
    
    if (orderType === 'both') {
      if (isOrdered) {
        const orderedBoxQty = Number(item.ordered_box_quantity || item.box_quantity || 0);
        const orderedSingleQty = Number(item.ordered_single_quantity || item.single_quantity || 0);
        return `${orderedBoxQty} (B)<br/>${orderedSingleQty} (S)`;
      } else {
        const fulfilledBoxQty = Number(item.fulfilled_box_quantity || 0);
        const fulfilledSingleQty = Number(item.fulfilled_single_quantity || 0);
        return `${fulfilledBoxQty} (B)<br/>${fulfilledSingleQty} (S)`;
      }
    } else {
      // For single or box orders, show the regular quantity
      const qty = isOrdered ? 
        (Number(item.quantity || 0)) : 
        (Number(item.fulfilled_quantity || item.fulfilledQuantity || 0));
      return qty.toString();
    }
  };
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch invoice data, company information, and invoice settings in parallel
        const [invoiceResponse, companyResponse, invoiceSettingsResponse] = await Promise.all([
          api.get(`/invoices/${id}`),
          api.get('/company'),
          api.get('/invoice-settings')
        ]);
        
        if (invoiceResponse.data && invoiceResponse.data.success) {
          setInvoice(invoiceResponse.data.data);
        } else {
          setError('Invoice not found');
        }
        
        if (companyResponse.data && companyResponse.data.success) {
          setCompanyInfo(companyResponse.data.data);
        }

        if (invoiceSettingsResponse.data && invoiceSettingsResponse.data.success) {
          setInvoiceSettings(invoiceSettingsResponse.data.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load invoice data');
      } finally {
        setLoading(false);
      }
    };
    
    if (!authLoading && isAuthenticated && id) {
      fetchData();
    }
  }, [authLoading, isAuthenticated, id]);
  
  const handleBack = () => {
    navigate('/invoices');
  };
  
  const handleEdit = () => {
    navigate(`/invoices/edit/${id}`);
  };
  
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    try {
      // In a real application, we would call the API
      // await api.delete(`/invoices/${id}`);
      
      // For now, we'll just navigate back
      navigate('/invoices');
    } catch (error) {
      console.error('Error deleting invoice:', error);
    }
  };
  
  const handlePrint = () => {
    window.print();
  };

  // Handle print from preview dialog
  const handlePrintFromPreview = () => {
    // Create a new window with the PDF content for printing
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Invoice ${invoice.invoiceNumber || invoice.invoice_number}</title>
          <style>
            body { margin: 0; font-family: Arial, sans-serif; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${pdfPreviewContent}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      printWindow.close();
    }
  };
  
  const handleEmail = () => {
    // In a real application, we would implement email functionality
    console.log('Email invoice:', invoice?.invoiceNumber);
  };
  
  // Create a PDF-specific invoice layout
  const createInvoicePdf = () => {
    if (!invoice) return '';
    
    // Helper function to format currency for PDF (use settings currency)
    const formatCurrencyPdf = (amount: number) => {
      // Use the same formatting as the main component
      return formatCurrency(amount);
    };
    
    // Helper function to format date
    const formatDateSimple = (dateString: string) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };
    
    // Use the same calculation functions for consistent results
    const subtotal = calculateInvoiceSubtotal();
    const taxAmount = calculateInvoiceTax();
    const refundAmount = calculateInvoiceRefunds();
    const totalAmount = calculateInvoiceTotal();
    const { totalItems, totalBoxQty, totalSingleQty } = calculateTotalQuantities();
    
    // Get company logo URL
    const companyLogoUrl = companyInfo?.logo ? getImageUrl(companyInfo.logo) : null;
    
    
    // Create table header for repeating on each page
    const createRepeatingTableHeader = () => `
      <thead style="display: table-header-group;">
        <tr style="background-color: #f8f9fa; break-inside: avoid;">
          <th style="border-bottom: 1px solid #000; padding: 8px 5px; text-align: left; width: 12%; font-size: 11px; font-weight: bold;">SKU</th>
          <th style="border-bottom: 1px solid #000; padding: 8px 5px; text-align: left; width: 25%; font-size: 11px; font-weight: bold;">Item</th>
          <th style="border-bottom: 1px solid #000; padding: 8px 5px; text-align: left; width: 10%; font-size: 11px; font-weight: bold;">Size</th>
          <th style="border-bottom: 1px solid #000; padding: 8px 5px; text-align: left; width: 8%; font-size: 11px; font-weight: bold;">Ord. Type</th>
          <th style="border-bottom: 1px solid #000; padding: 8px 5px; text-align: right; width: 8%; font-size: 11px; font-weight: bold;">Ordered Qty</th>
          <th style="border-bottom: 1px solid #000; padding: 8px 5px; text-align: right; width: 8%; font-size: 11px; font-weight: bold;">Fulfilled Qty</th>
          <th style="border-bottom: 1px solid #000; padding: 8px 5px; text-align: right; width: 12%; font-size: 11px; font-weight: bold;">Unit Price</th>
          <th style="border-bottom: 1px solid #000; padding: 8px 5px; text-align: right; width: 7%; font-size: 11px; font-weight: bold;">Tax (%)</th>
          <th style="border-bottom: 1px solid #000; padding: 8px 5px; text-align: right; width: 10%; font-size: 11px; font-weight: bold;">Total</th>
        </tr>
      </thead>
    `;
    
    // Create PDF content with clean HTML - using 12px base font size throughout
    return `
      <style>
        @page {
          margin: 10mm 5mm 20mm 10mm;
          size: A4;
          @bottom-right {
            content: "Page " counter(page) " of " counter(pages);
            font-size: 10px;
            color: #666;
            margin-right: 10mm;
          }
        }
        
        body {
          font-family: Arial, sans-serif;
          font-size: 12px;
          color: #333;
          line-height: 1.4;
        }
        
        .invoice-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
          margin-bottom: 20px;
        }
        
        .invoice-table thead {
          display: table-header-group;
        }
        
        .invoice-table tbody {
          display: table-row-group;
        }
        
        .invoice-table thead th {
          background-color: #f8f9fa;
          border-bottom: 1px solid #000;
          padding: 8px 5px;
          font-weight: bold;
          font-size: 11px;
        }
        
        .invoice-table tbody td {
          border-bottom: 1px solid #000;
          padding: 8px 5px;
          font-size: 11px;
        }
        
        .invoice-table tbody tr {
          break-inside: avoid;
        }
        
        .no-break {
          break-inside: avoid;
        }
        
        @media print {
          .invoice-table thead {
            display: table-header-group;
          }
        }
      </style>
      
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 5px 15px; color: #333; font-size: 12px;">
        <!-- Company Header Section with Company Info Left, Logo Center Top, Invoice Title Right -->
        <div style="margin-bottom: 20px; position: relative; min-height: 120px;">
          <!-- Company Info Section - Left Aligned -->
          <div style="text-align: left; width: 35%; float: left;">
            <h1 style="color: #2c3e50; margin: 0 0 5px 0; font-size: 16px; text-transform: uppercase; font-weight: bold;">${companyInfo?.name || 'Your Company'}</h1>
            <div style="font-size: 12px; color: #555;">
              <div>${companyInfo?.address || ''}</div>
              <div>${companyInfo?.city || ''}${companyInfo?.state ? `, ${companyInfo.state}` : ''} ${companyInfo?.zip_code || ''} ${companyInfo?.country || ''}</div>
              <div>Phone: ${companyInfo?.phone || ''}</div>
              <div>Email: ${companyInfo?.email || ''}</div>
              ${companyInfo?.tax_id ? `<div>Tax No: ${companyInfo.tax_id}</div>` : ''}
              ${companyInfo?.business_registration_number ? `<div>Reg No: ${companyInfo.business_registration_number}</div>` : ''}
            </div>
          </div>
          
          <!-- Logo Centered at Top -->
          ${companyLogoUrl ? `
          <div style="text-align: center; position: absolute; left: 50%; top: 0; transform: translateX(-50%); z-index: 1;">
            <img src="${companyLogoUrl}" alt="Company Logo" 
                 style="max-width: 160px; max-height: 110px; object-fit: contain;" />
          </div>
          ` : ''}
          
          <!-- Invoice Title - Top Right -->
          <div style="text-align: right; width: 35%; float: right;">
            <h2 style="color: #3498db; margin: 0 0 10px 0; font-size: 24px; font-weight: 600;">INVOICE</h2>
          </div>
          
          <!-- Clear float -->
          <div style="clear: both;"></div>
        </div>
        
        <!-- Invoice Details Horizontal Bar -->
        <div style="text-align: center; margin-bottom: 15px; padding: 8px; background-color: #f8f9fa; border-radius: 4px;">
          <span style="font-weight: bold; color: #3498db; font-size: 14px;">Invoice #${invoice.invoiceNumber || invoice.invoice_number}</span>
          <span style="margin-left: 20px; font-size: 12px;">
            Status: <span style="color: ${invoice.status === 'paid' ? '#27ae60' : invoice.status === 'overdue' ? '#e74c3c' : '#f39c12'}; font-weight: bold;">
              ${invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
            </span>
          </span>
        </div>
        
        <!-- Customer and Invoice Info Section -->
        <div style="margin-bottom: 15px; position: relative;">
          <!-- Bill To Section (Left) -->
          <div style="width: 48%; float: left;">
            <div style="font-weight: bold; margin-bottom: 3px; color: #7f8c8d; font-size: 12px; border-bottom: 1px solid #eee; padding-bottom: 3px;">BILL TO:</div>
            <div style="font-weight: bold; font-size: 13px;">${invoice.customerName || invoice.customer_name || 'Customer'}</div>
            <div style="font-size: 12px;">${invoice.customerAddress || invoice.customer_address || ''}</div>
            <div style="font-size: 12px;">${invoice.customerCity || invoice.customer_city || ''}${invoice.customerState || invoice.customer_state ? `, ${invoice.customerState || invoice.customer_state}` : ''} ${invoice.customerZipCode || invoice.customer_zip_code || ''}</div>
            <div style="font-size: 12px;">${invoice.customerCountry || invoice.customer_country || ''}</div>
            <div style="font-size: 12px;">${invoice.customerEmail || invoice.customer_email || ''}</div>
            <div style="font-size: 12px;">${invoice.customerPhone || invoice.customer_phone || ''}</div>
          </div>
          
          <!-- Invoice Details Section (Right Corner) -->
          <div style="position: absolute; right: 0; top: 0; width: 20%; text-align: left;">
            <div style="margin-bottom: 10px;">
              <div style="font-weight: bold; margin-bottom: 3px; color: #7f8c8d; font-size: 12px; border-bottom: 1px solid #eee; padding-bottom: 3px;">INVOICE DETAILS:</div>
              <div style="font-size: 12px;"><strong>Invoice Date:</strong> ${formatDateSimple(invoice.issueDate || invoice.issue_date || invoice.invoiceDate || invoice.invoice_date)}</div>
              <!-- <div style="font-size: 12px;"><strong>Due Date:</strong> ${formatDateSimple(invoice.dueDate || invoice.due_date)}</div> -->
              <!-- <div style="font-size: 12px;"><strong>Payment Terms:</strong> ${invoice.paymentTerms || invoice.payment_terms || 'Net 30'}</div> -->
            </div>
            
            ${(invoice.orderId > 0 || invoice.order_id > 0) ? `
            <div>
              <div style="font-weight: bold; margin-bottom: 3px; color: #7f8c8d; font-size: 12px; border-bottom: 1px solid #eee; padding-bottom: 3px;">ORDER INFORMATION:</div>
              <div style="font-size: 12px;"><strong>Order #:</strong> ${invoice.orderNumber || invoice.order_number}</div>
              <div style="font-size: 12px;"><strong>Order Date:</strong> ${formatDateSimple(invoice.orderDate || invoice.order_date)}</div>
            </div>
            ` : ''}
          </div>
          
          <!-- Clear float -->
          <div style="clear: both;"></div>
        </div>
        
        <div style="margin-bottom: 15px;">
          <div style="font-weight: bold; margin-bottom: 5px; color: #7f8c8d; font-size: 12px;">Items:</div>
          <table class="invoice-table" style="width: 100%; border-collapse: collapse; font-size: 12px;">
            ${createRepeatingTableHeader()}
            <tbody>
              ${invoice.items?.map((item: any, index: number) => {
                const orderedQuantity = Number(item.quantity || 0);
                const fulfilledQuantity = Number(item.fulfilled_quantity || item.fulfilledQuantity || 0);
                const unitPrice = Number(item.unit_price || item.unitPrice || 0);
                const qtyInBox = Number(item.qty_per_box || item.qtyPerBox || 1);
                const boxPrice = unitPrice * qtyInBox;
                const taxRate = Number(item.tax_rate || item.tax || 0);
                const orderType = item.order_type || 'box';
                const orderTypeDisplay = orderType.charAt(0).toUpperCase() + orderType.slice(1);
                
                // Use the same calculation functions for consistent results
                const itemTotal = calculateItemTotal(item);
                
                return `
                  <tr style="break-inside: avoid;">
                    <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: left; font-size: 11px;">${item.sku || item.product_sku || 'N/A'}</td>
                    <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: left; font-size: 11px;">${item.product_name || item.productName || item.description || 'Unknown Product'}</td>
                    <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: left; font-size: 11px;">${(item.product_unit_size || item.unit_size || '')} x ${(item.qty_per_box || item.qtyPerBox || 0)}</td>
                    <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: left; font-size: 11px;">${orderTypeDisplay}</td>
                    <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: right; font-size: 11px;">${formatQuantityDisplayPdf(item, true)}</td>
                    <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: right; font-size: 11px;">${formatQuantityDisplayPdf(item, false)}</td>
                    <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: right; font-size: 11px;">${formatCurrencyPdf(unitPrice)}</td>
                    <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: right; font-size: 11px;">${taxRate}%</td>
                    <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: right; font-size: 11px;">${formatCurrencyPdf(itemTotal)}</td>
                  </tr>
                `;
              }).join('') || `<tr><td colspan="9" style="text-align: center; padding: 10px; font-size: 11px;">No items</td></tr>`}
              
              ${invoice.refunds && invoice.refunds.length > 0 ? invoice.refunds.map((refund: any) => `
                <tr style="break-inside: avoid; background-color: #fff5f5;">
                  <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: left; font-size: 11px;" colspan="2">
                    <strong style="color: #dc2626;">Refund</strong>
                    ${refund.reason ? `<br><span style="font-size: 10px; color: #6b7280;">${refund.reason}</span>` : ''}
                  </td>
                  <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: center; font-size: 11px;" colspan="6">-</td>
                  <td style="border-bottom: 1px solid #ddd; padding: 8px 5px; text-align: right; font-size: 11px; color: #dc2626; font-weight: bold;">-${formatCurrency(parseFloat(refund.refund_amount))}</td>
                </tr>
              `).join('') : ''}
            </tbody>
          </table>
        </div>
        
        <div class="no-break" style="display: flex; justify-content: space-between; margin-top: 20px;">
          <!-- Delivery Summary Section (Left) -->
          <div style="width: 200px;">
            <div style="font-weight: bold; margin-bottom: 8px; color: #666; font-size: 11px;">DELIVERY SUMMARY</div>
            <table style="width: 100%; border-collapse: collapse; font-size: 10px; background-color: #f8f9fa; border: 1px solid #e9ecef; border-radius: 4px;">
              <tr>
                <td style="padding: 6px 8px; font-weight: bold; text-align: left; border-bottom: 1px solid #e9ecef;">Total Items:</td>
                <td style="padding: 6px 8px; text-align: right; border-bottom: 1px solid #e9ecef;">${totalItems}</td>
              </tr>
              ${totalBoxQty > 0 ? `
              <tr>
                <td style="padding: 6px 8px; font-weight: bold; text-align: left; border-bottom: 1px solid #e9ecef;">Box Quantities:</td>
                <td style="padding: 6px 8px; text-align: right; border-bottom: 1px solid #e9ecef;">${totalBoxQty}</td>
              </tr>
              ` : ''}
              ${totalSingleQty > 0 ? `
              <tr>
                <td style="padding: 6px 8px; font-weight: bold; text-align: left;">Single Quantities:</td>
                <td style="padding: 6px 8px; text-align: right;">${totalSingleQty}</td>
              </tr>
              ` : ''}
            </table>
          </div>

          <!-- Price Summary Section (Right) -->
          <div style="width: 250px;">
            <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <tr>
                <td style="padding: 4px 0; font-weight: bold; text-align: left;">Subtotal:</td>
                <td style="padding: 4px 0; text-align: right;">${formatCurrencyPdf(subtotal)}</td>
              </tr>
              <tr>
                <td style="padding: 4px 0; font-weight: bold; text-align: left;">Tax:</td>
                <td style="padding: 4px 0; text-align: right;">${formatCurrencyPdf(taxAmount)}</td>
              </tr>
              ${refundAmount > 0 ? `
              <tr>
                <td style="padding: 4px 0; font-weight: bold; text-align: left; color: #dc2626;">Refunds:</td>
                <td style="padding: 4px 0; text-align: right; color: #dc2626;">-${formatCurrencyPdf(refundAmount)}</td>
              </tr>
              ` : ''}
              <tr style="font-size: 14px; font-weight: bold;">
                <td style="padding: 6px 0; border-top: 1px solid #ddd; text-align: left;">Total:</td>
                <td style="padding: 6px 0; border-top: 1px solid #ddd; text-align: right;">${formatCurrencyPdf(totalAmount)}</td>
              </tr>
            </table>
          </div>
        </div>
        
        
        <!-- Bank Details and Terms Section -->
        ${(invoiceSettings?.showBankDetails && (invoiceSettings?.bankName || invoiceSettings?.accountName || invoiceSettings?.accountNumber || invoiceSettings?.routingNumber)) || invoiceSettings?.defaultTerms ? `
          <div class="no-break" style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #e0e0e0; display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
            ${invoiceSettings?.showBankDetails && (invoiceSettings?.bankName || invoiceSettings?.accountName || invoiceSettings?.accountNumber || invoiceSettings?.routingNumber) ? `
              <!-- Bank Details -->
              <div>
                <div style="font-weight: 600; margin-bottom: 8px; color: #666; font-size: 11px;">BANK DETAILS</div>
                <div style="background-color: #f8f9fa; padding: 12px; border-radius: 4px; border: 1px solid #e9ecef; font-size: 11px;">
                  ${invoiceSettings?.bankName ? `<div style="margin-bottom: 4px;"><strong>Bank:</strong> ${invoiceSettings.bankName}</div>` : ''}
                  ${invoiceSettings?.accountName ? `<div style="margin-bottom: 4px;"><strong>Account Name:</strong> ${invoiceSettings.accountName}</div>` : ''}
                  ${invoiceSettings?.accountNumber ? `<div style="margin-bottom: 4px;"><strong>Account Number:</strong> ${invoiceSettings.accountNumber}</div>` : ''}
                  ${invoiceSettings?.routingNumber ? `<div><strong>Sort Code:</strong> ${invoiceSettings.routingNumber}</div>` : ''}
                </div>
              </div>
            ` : ''}
            
            ${invoiceSettings?.defaultTerms ? `
              <!-- Terms & Conditions -->
              <div>
                <div style="font-weight: 600; margin-bottom: 8px; color: #666; font-size: 11px;">TERMS & CONDITIONS</div>
                <div style="background-color: #f8f9fa; padding: 12px; border-radius: 4px; border: 1px solid #e9ecef; font-size: 11px; line-height: 1.5;">
                  ${invoiceSettings.defaultTerms}
                </div>
              </div>
            ` : ''}
          </div>
        ` : ''}
        
        <!-- Signature Section -->
        <div class="no-break" style="margin-top: 25px; padding-top: 20px; border-top: 1px solid #e0e0e0;">
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 30px;">
            <!-- Authorized Signature -->
            <div style="text-align: center;">
              <div style="border-bottom: 1px solid #333; width: 200px; margin: 0 auto 10px auto; height: 40px;"></div>
              <div style="font-size: 12px; color: #666;">Authorized Signature</div>
              <div style="font-size: 11px; color: #999; margin-top: 5px;">Date: ___________</div>
            </div>
            
            <!-- Customer Signature -->
            <div style="text-align: center;">
              <div style="border-bottom: 1px solid #333; width: 200px; margin: 0 auto 10px auto; height: 40px;"></div>
              <div style="font-size: 12px; color: #666;">Customer Signature</div>
              <div style="font-size: 11px; color: #999; margin-top: 5px;">Date: ___________</div>
            </div>
          </div>
        </div>
        
        <div class="no-break" style="margin-top: 15px; text-align: center; color: #7f8c8d; font-size: 12px;">
          <p>Thank you for your business!</p>
        </div>
      </div>
      
    `;
  };
  
  // Show PDF preview (still uses frontend HTML generation for preview)
  const handleShowPreview = () => {
    // Create the PDF content for preview
    const pdfContent = createInvoicePdf();
    setPdfPreviewContent(pdfContent);
    setPreviewDialogOpen(true);
  };
  
  // Handle PDF download using Puppeteer backend
  const handleDownload = async () => {
    setDownloading(true);
    try {
      console.log('Starting PDF download for invoice ID:', id);
      
      // Call the backend PDF generation endpoint (temporarily using PDFKit)
      const response = await api.get(`/invoices/${id}/pdf-kit`, {
        responseType: 'blob', // Important: Tell axios to handle binary data
        headers: {
          'Accept': 'application/pdf'
        }
      });

      console.log('PDF response received:', response.status, response.headers);

      // Check if response is valid
      if (!response.data) {
        throw new Error('No PDF data received from server');
      }

      // Create blob from response data
      const blob = new Blob([response.data], { type: 'application/pdf' });
      console.log('Blob created:', blob.size, 'bytes');
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoice.invoiceNumber || invoice.invoice_number}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Close the preview dialog if it's open
      setPreviewDialogOpen(false);
      
      console.log('Invoice downloaded successfully');
    } catch (error: any) {
      console.error('Error downloading invoice:', error);
      
      // Show detailed error to help debug
      if (error?.response) {
        console.error('Response error:', error.response.status, error.response.data);
        alert(`PDF generation failed: ${error.response.status} - ${error.response.statusText}`);
      } else if (error?.request) {
        console.error('Request error:', error.request);
        alert('No response from server. Please check if the backend is running.');
      } else {
        console.error('Error:', error?.message || error);
        alert(`Error: ${error?.message || 'Unknown error occurred'}`);
      }
    } finally {
      setDownloading(false);
    }
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'draft':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };
  
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error || !invoice) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Invoice not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Back to Invoices
        </Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h4">
            Invoice {invoice.invoiceNumber}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleShowPreview}
            disabled={downloading}
          >
            {downloading ? 'Generating...' : 'Download PDF'}
          </Button>
        </Box>
      </Box>
      
      <Paper sx={{ p: 3, mb: 3 }} className="invoice-printable" ref={invoiceRef}>
        {/* Company Header with Company Info Left, Logo Center Top, Invoice Title Right */}
        <Box sx={{ mb: 4, position: 'relative', minHeight: '120px' }}>
          {/* Company Info Section - Left Aligned */}
          <Box sx={{ 
            textAlign: 'left', 
            width: { xs: '100%', md: '35%' },
            float: { xs: 'none', md: 'left' },
            mb: { xs: 2, md: 0 }
          }}>
            <Typography variant="h6" sx={{ color: '#2c3e50', fontWeight: 'bold', textTransform: 'uppercase', mb: 1 }}>
              {companyInfo?.name || 'Your Company'}
            </Typography>
            <Box sx={{ color: '#555', fontSize: '0.875rem' }}>
              <Typography variant="body2">{companyInfo?.address || ''}</Typography>
              <Typography variant="body2">
                {companyInfo?.city || ''}{companyInfo?.state ? `, ${companyInfo.state}` : ''} {companyInfo?.zip_code || ''} {companyInfo?.country || ''}
              </Typography>
              <Typography variant="body2">
                Phone: {companyInfo?.phone || ''}
              </Typography>
              <Typography variant="body2">
                Email: {companyInfo?.email || ''}
              </Typography>
              {companyInfo?.tax_id && (
                <Typography variant="body2">
                  Tax No: {companyInfo.tax_id}
                </Typography>
              )}
              {companyInfo?.business_registration_number && (
                <Typography variant="body2">
                  Reg No: {companyInfo.business_registration_number}
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* Logo Centered at Top */}
          {companyInfo?.logo && (
            <Box sx={{ 
              textAlign: 'center',
              position: { xs: 'static', md: 'absolute' },
              left: '50%',
              top: 0,
              transform: { xs: 'none', md: 'translateX(-50%)' },
              zIndex: 1,
              mb: { xs: 2, md: 0 }
            }}>
              <Box
                component="img"
                src={getImageUrl(companyInfo.logo)}
                alt="Company Logo"
                sx={{
                  maxWidth: '160px',
                  maxHeight: '110px',
                  objectFit: 'contain',
                  display: 'inline-block'
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </Box>
          )}
          
          {/* Invoice Title - Top Right */}
          <Box sx={{ 
            textAlign: 'right', 
            width: { xs: '100%', md: '35%' },
            float: { xs: 'none', md: 'right' },
            mb: { xs: 2, md: 0 }
          }}>
            <Typography variant="h4" sx={{ color: '#3498db', mb: 1, fontWeight: 600 }}>
              INVOICE
            </Typography>
          </Box>
          
          {/* Clear float */}
          <Box sx={{ clear: 'both' }} />
        </Box>
        
        {/* Invoice Details Horizontal Bar */}
        <Box sx={{ 
          textAlign: 'center', 
          mb: 4, 
          p: 1, 
          bgcolor: '#f8f9fa', 
          borderRadius: 1
        }}>
          <Typography variant="h6" sx={{ color: '#3498db', fontWeight: 'bold', display: 'inline', mr: 2 }}>
            Invoice #{invoice.invoiceNumber || invoice.invoice_number}
          </Typography>
          <Typography variant="body2" sx={{ display: 'inline' }}>
            Status: <Box component="span" sx={{ 
              color: invoice.status === 'paid' ? '#27ae60' : invoice.status === 'overdue' ? '#e74c3c' : '#f39c12',
              fontWeight: 'bold'
            }}>
              {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
            </Box>
          </Typography>
        </Box>

        {/* Invoice Information Section */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', mb: 4 }}>
          <Box>
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary" component="span" sx={{ mr: 1 }}>
                Invoice #:
              </Typography>
              <Typography variant="body1" component="span" fontWeight="medium" color="primary">
                {invoice.invoiceNumber || invoice.invoice_number}
              </Typography>
            </Box>
            {(invoice.orderId > 0 || invoice.order_id > 0) && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary" component="span" sx={{ mr: 1 }}>
                  Order #:
                </Typography>
                <Button 
                  color="primary" 
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 'medium', fontSize: '1rem' }}
                  onClick={() => navigate(`/orders/${invoice.orderId || invoice.order_id}`)}
                >
                  {invoice.orderNumber || invoice.order_number}
                </Button>
                {(invoice.orderDate || invoice.order_date) && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" color="text.secondary" component="span" sx={{ mr: 1 }}>
                      Order Date:
                    </Typography>
                    <Typography variant="body2" component="span">
                      {formatDate(invoice.orderDate || invoice.order_date)}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{ mt: 1 }}>
              <Chip 
                label={invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)} 
                color={getStatusColor(invoice.status)}
              />
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ mb: 4, position: 'relative' }}>
          {/* Bill To Section (Left) */}
          <Box sx={{ width: { xs: '100%', md: '48%' }, float: { xs: 'none', md: 'left' }, mb: { xs: 3, md: 0 } }}>
            <Typography variant="body2" color="text.secondary">
              Bill To
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {invoice.customerName || invoice.customer_name || 'Customer'}
            </Typography>
            <Typography variant="body2">
              {invoice.customerAddress || invoice.customer_address || ''}
            </Typography>
            {(invoice.customer_city || invoice.customerCity) && (
              <Typography variant="body2">
                {invoice.customer_city || invoice.customerCity}{invoice.customer_state || invoice.customerState ? `, ${invoice.customer_state || invoice.customerState}` : ''} {invoice.customer_postal_code || invoice.customerPostalCode || ''}
              </Typography>
            )}
            {(invoice.customer_country || invoice.customerCountry) && (
              <Typography variant="body2">
                {invoice.customer_country || invoice.customerCountry}
              </Typography>
            )}
            <Typography variant="body2">
              {invoice.customerEmail || invoice.customer_email || ''}
            </Typography>
            {(invoice.customer_phone || invoice.customerPhone) && (
              <Typography variant="body2">
                {invoice.customer_phone || invoice.customerPhone}
              </Typography>
            )}
          </Box>
          
          {/* Invoice Details Section (Right Corner) */}
          <Box sx={{ 
            position: { xs: 'static', md: 'absolute' },
            right: 0,
            top: 0,
            width: { xs: '100%', md: '20%' },
            textAlign: 'left'
          }}>
            <Typography variant="body2" color="text.secondary">
              Invoice Details
            </Typography>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Invoice Date:</strong> {formatDate(invoice.issueDate || invoice.issue_date || invoice.invoiceDate || invoice.invoice_date)}
            </Typography>
            {/* <Typography variant="body2" sx={{ mb: 0.5 }}>
              <strong>Due Date:</strong> {formatDate(invoice.dueDate || invoice.due_date)}
            </Typography> */}
            {/* <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>Payment Terms:</strong> {invoice.paymentTerms || invoice.payment_terms || 'Net 30'}
            </Typography> */}
            
            {(invoice.orderId > 0 || invoice.order_id > 0) && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Order Information
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Order #:</strong> {invoice.orderNumber || invoice.order_number}
                </Typography>
                <Typography variant="body2">
                  <strong>Order Date:</strong> {formatDate(invoice.orderDate || invoice.order_date)}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Clear float */}
          <Box sx={{ clear: 'both' }} />
        </Box>
        
        {/* Removed duplicate invoice/order number section */}
        
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.default' }}>
                <TableCell>SKU</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Ord. Type</TableCell>
                <TableCell align="right">Ordered Qty</TableCell>
                <TableCell align="right">Fulfilled Qty</TableCell>
                <TableCell align="right">Unit Price</TableCell>
                {/* <TableCell align="right">Box Price</TableCell> */}
                <TableCell align="right">Tax (%)</TableCell>
                <TableCell align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoice.items.map((item: any) => {
                return (
                <TableRow key={item.id}>
                  <TableCell>{item.sku || item.product_sku || 'N/A'}</TableCell>
                  <TableCell>{item.product_name || item.productName || item.description || 'Unknown Product'}</TableCell>
                  <TableCell>
                    {(item.product_unit_size || item.unit_size || '') + 
                     ' x ' + 
                     (item.qty_per_box || item.qtyPerBox || 0)}
                  </TableCell>
                  <TableCell>{(() => {
                    const orderType = item.order_type || 'box';
                    return orderType.charAt(0).toUpperCase() + orderType.slice(1);
                  })()}</TableCell>
                  <TableCell align="right">{formatQuantityDisplay(item, true)}</TableCell>
                  <TableCell align="right">{formatQuantityDisplay(item, false)}</TableCell>
                  <TableCell align="right">{formatCurrency(Number(item.unit_price || item.unitPrice) || 0)}</TableCell>
                  {/* <TableCell align="right">{(() => {
                    const unitPrice = Number(item.unit_price || item.unitPrice) || 0;
                    const qtyInBox = Number(item.qty_per_box || item.qtyPerBox) || 0;
                    const boxPrice = unitPrice * qtyInBox;
                    return formatCurrency(boxPrice);
                  })()}</TableCell> */}
                  <TableCell align="right">{(Number(item.tax_rate || item.tax) || 0)}%</TableCell>
                  <TableCell align="right">{formatCurrency(calculateItemTotal(item))}</TableCell>
                </TableRow>
                );
              })}
              
              {/* Refunds */}
              {invoice.refunds && invoice.refunds.length > 0 && invoice.refunds.map((refund: any) => (
                <TableRow key={`refund-${refund.id}`} sx={{ backgroundColor: '#fff5f5' }}>
                  <TableCell colSpan={2}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        Refund
                      </Typography>
                      {refund.reason && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {refund.reason}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">-</TableCell>
                  <TableCell align="center">-</TableCell>
                  <TableCell align="center">-</TableCell>
                  <TableCell align="center">-</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    -{formatCurrency(parseFloat(refund.refund_amount))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, mt: 3 }}>
          {/* Delivery Summary Section (Left) */}
          <Box sx={{ width: '250px' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
              DELIVERY SUMMARY
            </Typography>
            <Box sx={{ 
              bgcolor: '#f8f9fa', 
              border: '1px solid #e9ecef',
              borderRadius: 1,
              p: 1.5
            }}>
              {(() => {
                const { totalItems, totalBoxQty, totalSingleQty } = calculateTotalQuantities();
                return (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>Total Items:</Typography>
                      <Typography variant="caption">{totalItems}</Typography>
                    </Box>
                    {totalBoxQty > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Box Quantities:</Typography>
                        <Typography variant="caption">{totalBoxQty}</Typography>
                      </Box>
                    )}
                    {totalSingleQty > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Single Quantities:</Typography>
                        <Typography variant="caption">{totalSingleQty}</Typography>
                      </Box>
                    )}
                  </>
                );
              })()}
            </Box>
          </Box>

          {/* Price Summary Section (Right) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '300px' }}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1">{formatCurrency(calculateInvoiceSubtotal())}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '300px' }}>
              <Typography variant="body1">Tax:</Typography>
              <Typography variant="body1">{formatCurrency(calculateInvoiceTax())}</Typography>
            </Box>
            {calculateInvoiceRefunds() > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '300px' }}>
                <Typography variant="body1" color="error">Refunds:</Typography>
                <Typography variant="body1" color="error">-{formatCurrency(calculateInvoiceRefunds())}</Typography>
              </Box>
            )}
            <Divider sx={{ width: '300px', my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '300px' }}>
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6">{formatCurrency(calculateInvoiceTotal())}</Typography>
            </Box>
          </Box>
        </Box>
        
        {invoice.status === 'paid' && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Payment Information
            </Typography>
            <Typography variant="body1">
              Method: {invoice.paymentMethod ? invoice.paymentMethod.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) : 'N/A'}
            </Typography>
            <Typography variant="body1">
              Date: {formatDate(invoice.paymentDate)}
            </Typography>
          </Box>
        )}
        

        {/* Bank Details and Terms Section */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
          gap: 4, 
          mb: 4,
          mt: 4,
          pt: 3,
          borderTop: '0.5px solid #e0e0e0'
        }}>
          {/* Bank Details */}
          {invoiceSettings?.showBankDetails && (
            invoiceSettings?.bankName || 
            invoiceSettings?.accountName || 
            invoiceSettings?.accountNumber || 
            invoiceSettings?.routingNumber
          ) && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: '9px' }}>
                BANK DETAILS
              </Typography>
              <Box sx={{ 
                bgcolor: '#f8f9fa', 
                p: 2, 
                borderRadius: 1,
                border: '1px solid #e9ecef'
              }}>
                {invoiceSettings?.bankName && (
                  <Typography variant="caption" sx={{ mb: 0.5, fontSize: '8px', color: '#333333' }}>
                    <strong>Bank:</strong> {invoiceSettings.bankName}
                  </Typography>
                )}
                {invoiceSettings?.accountName && (
                  <Typography variant="caption" sx={{ mb: 0.5, fontSize: '8px', color: '#333333' }}>
                    <strong>Account Name:</strong> {invoiceSettings.accountName}
                  </Typography>
                )}
                {invoiceSettings?.accountNumber && (
                  <Typography variant="caption" sx={{ mb: 0.5, fontSize: '8px', color: '#333333' }}>
                    <strong>Account Number:</strong> {invoiceSettings.accountNumber}
                  </Typography>
                )}
                {invoiceSettings?.routingNumber && (
                  <Typography variant="caption" sx={{ fontSize: '8px', color: '#333333' }}>
                    <strong>Sort Code:</strong> {invoiceSettings.routingNumber}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Terms & Conditions */}
          {invoiceSettings?.defaultTerms && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, fontWeight: 600, fontSize: '9px' }}>
                TERMS & CONDITIONS
              </Typography>
              <Box sx={{ 
                bgcolor: '#f8f9fa', 
                p: 2, 
                borderRadius: 1,
                border: '1px solid #e9ecef'
              }}>
                <Typography variant="caption" sx={{ lineHeight: 1.6, fontSize: '8px', color: '#333333' }}>
                  {invoiceSettings.defaultTerms}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Typography variant="caption" color="text.secondary" align="center">
          Thank you for your business!
        </Typography>
        
        {/* Signature Section */}
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            {/* Authorized Signature */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Box
                sx={{
                  borderBottom: '1px solid #333',
                  width: 200,
                  height: 40,
                  mx: 'auto',
                  mb: 1
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Authorized Signature
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Date: ___________
              </Typography>
            </Box>
            
            {/* Customer Signature */}
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Box
                sx={{
                  borderBottom: '1px solid #333',
                  width: 200,
                  height: 40,
                  mx: 'auto',
                  mb: 1
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Customer Signature
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Date: ___________
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete invoice "{invoice.invoiceNumber}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* PDF Preview dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Invoice Preview</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={handleDownload}
                disabled={downloading}
              >
                {downloading ? 'Downloading...' : 'Download PDF'}
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, p: 2, border: '1px solid #eee', borderRadius: 1, bgcolor: '#fff' }}>
            <div 
              ref={pdfPreviewRef} 
              dangerouslySetInnerHTML={{ __html: pdfPreviewContent }} 
              style={{ width: '100%', minHeight: '500px' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceDetail;
